import { saveSettings, settings } from '../../engine/core/settings';
import { screens } from '../../engine/ui/screens';
import { openCustomEditor } from '../custom/editor';

/** The loaded world (null on the still menu) and how to start playing it. */
let current: { scene: string | null; startPlaying: (() => void) | null } = {
  scene: null,
  startPlaying: null,
};
let wired = false;

/**
 * Start-screen pickers. The quality choice is saved and applied by reloading; the
 * world choice reloads with ?scene=<id> (keeping other flags). Picking the world
 * that's already loaded simply starts playing. Call again when the loaded world changes.
 */
export function setupStartScreen(sceneName: string | null, startPlaying: (() => void) | null): void {
  current = { scene: sceneName, startPlaying };
  for (const b of document.querySelectorAll<HTMLButtonElement>('#quality button')) {
    b.classList.toggle('current', b.dataset.q === settings.quality);
  }
  for (const b of document.querySelectorAll<HTMLButtonElement>('#worlds button')) {
    b.classList.toggle('current', b.dataset.scene === sceneName);
  }
  if (wired) return;
  wired = true;
  for (const b of document.querySelectorAll<HTMLButtonElement>('#quality button')) {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      if (b.dataset.q === settings.quality) return;
      settings.quality = (b.dataset.q ?? 'high') as typeof settings.quality;
      saveSettings();
      const url = new URL(location.href);
      url.searchParams.delete('quality');
      location.href = url.toString();
    });
  }
  for (const b of document.querySelectorAll<HTMLButtonElement>('#worlds button')) {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      // Custom worlds: design them first (the editor's "Generate & play" loads them).
      if (b.dataset.scene === 'custom') {
        openCustomEditor();
        return;
      }
      if (b.dataset.scene === current.scene && current.startPlaying) {
        current.startPlaying();
        return;
      }
      const url = new URL(location.href);
      url.searchParams.set('scene', b.dataset.scene ?? 'verdant');
      url.searchParams.delete('cam');
      location.href = url.toString();
    });
  }
}

/**
 * The menu before any world is loaded, over a still picture of the menu world. Loading a
 * world compiles its shaders, which takes seconds on a first visit, so nothing loads until
 * the player picks a world, or asks for the live menu world with the button.
 */
export function showStillMenu(image: string, loadMenuWorld: () => void): void {
  setupStartScreen(null, null);
  screens.hideBoot();
  screens.stillMenu(true, image);
  screens.setPlaying(false);
  document.getElementById('load-menu-world')!.addEventListener(
    'click',
    (e) => {
      e.stopPropagation();
      screens.stillMenu(false);
      loadMenuWorld();
    },
    { once: true },
  );
}

/** A world's first-visit hints, shown once per browser. */
export function showHintsOnce(sceneName: string, hints: string[] | undefined): void {
  if (!hints) return;
  const key = `innerworlds.hints.${sceneName}`;
  let seen = false;
  try {
    seen = localStorage.getItem(key) === '1';
    localStorage.setItem(key, '1');
  } catch {
    // storage unavailable: show them every time
  }
  if (!seen) screens.hints(hints);
}
